<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import V4Button from 'next/button/Button.vue';
import Select from 'next/select/Select.vue';
import InputNext from 'next/input/Input.vue';
import Switch from 'next/switch/Switch.vue';
import SettingsFieldSection from 'next/Settings/SettingsFieldSection.vue';
import { buildWebhookPayloadExample } from '../helpers/webhookPayloadExample';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  eventName: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const PROPERTY_TYPES = computed(() => [
  {
    value: 'text',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.PROPERTY_TYPE_OPTIONS.TEXT'),
  },
  {
    value: 'number',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.PROPERTY_TYPE_OPTIONS.NUMBER'),
  },
  {
    value: 'boolean',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.PROPERTY_TYPE_OPTIONS.BOOLEAN'),
  },
  {
    value: 'conversation',
    label: t(
      'WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.PROPERTY_TYPE_OPTIONS.CONVERSATION'
    ),
  },
  {
    value: 'contact',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.PROPERTY_TYPE_OPTIONS.CONTACT'),
  },
  {
    value: 'agent',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.PROPERTY_TYPE_OPTIONS.AGENT'),
  },
  {
    value: 'inbox',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.PROPERTY_TYPE_OPTIONS.INBOX'),
  },
]);

const AGGREGATION_OPTIONS = computed(() => [
  {
    value: 'count',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.AGGREGATION_OPTIONS.COUNT'),
  },
  {
    value: 'sum',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.AGGREGATION_OPTIONS.SUM'),
  },
  {
    value: 'avg',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.AGGREGATION_OPTIONS.AVG'),
  },
  {
    value: 'min',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.AGGREGATION_OPTIONS.MIN'),
  },
  {
    value: 'max',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.AGGREGATION_OPTIONS.MAX'),
  },
]);

// Reports group by these names as the event's built-in record fields, so the API rejects
// properties named after them.
const RESERVED_PROPERTY_NAMES = [
  'user',
  'agent',
  'inbox',
  'contact',
  'conversation',
];

const propertyNameError = name =>
  RESERVED_PROPERTY_NAMES.includes(name.trim())
    ? t(
        'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.VALIDATION.PROP_NAME_RESERVED'
      )
    : '';

const BUILT_IN_DIMENSIONS = computed(() => [
  {
    value: 'user',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.BUILT_IN_DIMENSIONS.AGENT'),
  },
  {
    value: 'inbox',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.BUILT_IN_DIMENSIONS.INBOX'),
  },
  {
    value: 'contact',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.BUILT_IN_DIMENSIONS.CONTACT'),
  },
]);

const CHART_TYPES = computed(() => [
  {
    value: 'bar_chart',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.CHART_TYPE_OPTIONS.BAR_CHART'),
  },
  {
    value: 'line_chart',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.CHART_TYPE_OPTIONS.LINE_CHART'),
  },
  {
    value: 'pie_chart',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.CHART_TYPE_OPTIONS.PIE_CHART'),
  },
  {
    value: 'table',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.CHART_TYPE_OPTIONS.TABLE'),
  },
]);

const TIME_GRANULARITIES = computed(() => [
  {
    value: 'day',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.TIME_GRANULARITY_OPTIONS.DAY'),
  },
  {
    value: 'week',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.TIME_GRANULARITY_OPTIONS.WEEK'),
  },
  {
    value: 'month',
    label: t(
      'WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.TIME_GRANULARITY_OPTIONS.MONTH'
    ),
  },
  {
    value: 'year',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.TIME_GRANULARITY_OPTIONS.YEAR'),
  },
  {
    value: 'none',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.TIME_GRANULARITY_OPTIONS.NONE'),
  },
]);

const VALUE_MODES = computed(() => [
  { value: 'absolute', label: t('WGPT_CUSTOM_REPORTS.VALUE_MODES.ABSOLUTE') },
  {
    value: 'percentage',
    label: t('WGPT_CUSTOM_REPORTS.VALUE_MODES.PERCENTAGE'),
  },
]);

// Local rows keep unsaved (empty-name) rows alive in the UI.
// localSchemaRows is derived from props only for external updates (e.g. chat).
const localSchemaRows = ref([]);
let isInternalSchemaUpdate = false;

watch(
  () => props.modelValue?.event_schema?.properties,
  properties => {
    if (isInternalSchemaUpdate) return;
    const schema = properties || {};
    localSchemaRows.value = Object.entries(schema).map(([name, def]) => ({
      name,
      type: def.type || 'text',
      label: def.label || '',
      description: def.description || '',
    }));
  },
  { immediate: true }
);

const metrics = computed(() => props.modelValue?.metrics || []);

const dimensions = computed(() => props.modelValue?.dimensions || []);

function updateConfig(patch) {
  emit('update:modelValue', { ...props.modelValue, ...patch });
}

const defaultVisualization = computed({
  get: () => props.modelValue?.default_visualization || 'bar_chart',
  set: v => updateConfig({ default_visualization: v }),
});

const defaultGroupBy = computed({
  get: () => props.modelValue?.default_group_by || '',
  set: v => updateConfig({ default_group_by: v }),
});

const defaultTimeGranularity = computed({
  get: () => props.modelValue?.default_time_granularity || 'day',
  set: v => updateConfig({ default_time_granularity: v }),
});

const defaultValueMode = computed({
  get: () => props.modelValue?.default_value_mode || 'absolute',
  set: v => updateConfig({ default_value_mode: v }),
});

const showSummaryCards = computed({
  get: () => props.modelValue?.show_summary_cards ?? false,
  set: v => updateConfig({ show_summary_cards: v }),
});

const propertyNames = computed(() =>
  localSchemaRows.value
    .filter(p => p.name.trim() && p.type === 'number')
    .map(p => ({ value: p.name, label: p.label || p.name }))
);

const dimensionOptions = computed(() => [
  ...BUILT_IN_DIMENSIONS.value,
  ...localSchemaRows.value
    .filter(p => p.name.trim())
    .map(p => ({ value: p.name, label: p.label || p.name })),
]);

const webhookExampleJson = computed(() => {
  const eventName = props.modelValue?.event_name;
  if (!eventName) return '';
  const payload = buildWebhookPayloadExample(
    eventName,
    props.modelValue?.event_schema
  );
  return JSON.stringify(payload, null, 2);
});

const groupByOptions = computed(() => [
  { value: '', label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.NONE') },
  ...dimensions.value.map(d => ({
    value: d.property || d.source || '',
    label: d.label || d.property || d.source || '',
  })),
]);

function rebuildSchema(rows) {
  isInternalSchemaUpdate = true;
  const properties = {};
  rows.forEach(row => {
    if (row.name.trim()) {
      properties[row.name.trim()] = {
        type: row.type,
        label: row.label,
        ...(row.description ? { description: row.description } : {}),
      };
    }
  });
  updateConfig({ event_schema: { properties } });
  nextTick(() => {
    isInternalSchemaUpdate = false;
  });
}

function addProperty() {
  localSchemaRows.value = [
    ...localSchemaRows.value,
    { name: '', type: 'text', label: '', description: '' },
  ];
}

function removeProperty(index) {
  localSchemaRows.value = localSchemaRows.value.filter((_, i) => i !== index);
  rebuildSchema(localSchemaRows.value);
}

function updateProperty(index, field, value) {
  localSchemaRows.value = localSchemaRows.value.map((row, i) =>
    i === index ? { ...row, [field]: value } : row
  );
  rebuildSchema(localSchemaRows.value);
}

function addMetric() {
  const updated = [
    ...metrics.value,
    { key: '', aggregation: 'count', property: '', label: '' },
  ];
  updateConfig({ metrics: updated });
}

function removeMetric(index) {
  updateConfig({ metrics: metrics.value.filter((_, i) => i !== index) });
}

function updateMetric(index, field, value) {
  const updated = metrics.value.map((m, i) =>
    i === index ? { ...m, [field]: value } : m
  );
  if (field === 'label' && !updated[index].key) {
    updated[index].key = value
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  }
  if (field === 'aggregation' && value === 'count') {
    updated[index].property = '';
  }
  updateConfig({ metrics: updated });
}

function addDimension() {
  const updated = [...dimensions.value, { property: '', label: '' }];
  updateConfig({ dimensions: updated });
}

function removeDimension(index) {
  updateConfig({ dimensions: dimensions.value.filter((_, i) => i !== index) });
}

function updateDimension(index, selectedValue) {
  const builtIn = BUILT_IN_DIMENSIONS.value.find(
    d => d.value === selectedValue
  );
  const updated = dimensions.value.map((d, i) => {
    if (i !== index) return d;
    if (builtIn) {
      return { source: builtIn.value, label: builtIn.label };
    }
    const schemaProp = localSchemaRows.value.find(
      p => p.name === selectedValue
    );
    return {
      property: selectedValue,
      label: schemaProp?.label || selectedValue,
    };
  });
  updateConfig({ dimensions: updated });
}

function updateDimensionLabel(index, label) {
  const updated = dimensions.value.map((d, i) =>
    i === index ? { ...d, label } : d
  );
  updateConfig({ dimensions: updated });
}
</script>

<template>
  <div class="space-y-8">
    <section v-if="eventName">
      <SettingsFieldSection :label="t('WGPT_CUSTOM_REPORTS.FORM.EVENT_NAME')">
        <InputNext :model-value="eventName" disabled />
      </SettingsFieldSection>
    </section>

    <section>
      <div class="flex items-center justify-between mb-3">
        <div>
          <h4 class="text-heading-3 text-n-slate-12">
            {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.SCHEMA_TITLE') }}
          </h4>
          <p class="text-body-main text-n-slate-11">
            {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.SCHEMA_DESCRIPTION') }}
          </p>
        </div>
        <V4Button
          xs
          ghost
          blue
          icon="i-lucide-plus"
          :label="t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.ADD_PROPERTY')"
          @click="addProperty"
        />
      </div>

      <table
        class="min-w-full table-auto outline outline-1 -outline-offset-1 outline-n-strong rounded-xl"
      >
        <thead>
          <tr class="border-b border-n-strong">
            <th
              class="py-3 ltr:pl-4 rtl:pr-4 ltr:pr-4 rtl:pl-4 text-start text-heading-3 text-n-slate-12"
            >
              {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.PROPERTY_NAME') }}
            </th>
            <th
              class="py-3 ltr:pr-4 rtl:pl-4 text-start text-heading-3 text-n-slate-12"
            >
              {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.PROPERTY_TYPE') }}
            </th>
            <th
              class="py-3 ltr:pr-4 rtl:pl-4 text-start text-heading-3 text-n-slate-12"
            >
              {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.PROPERTY_LABEL') }}
            </th>
            <th
              class="py-3 ltr:pr-4 rtl:pl-4 text-start text-heading-3 text-n-slate-12"
            >
              {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.PROPERTY_DESCRIPTION') }}
            </th>
            <th class="py-3 ltr:pr-4 rtl:pl-4" />
          </tr>
        </thead>
        <tbody>
          <tr v-if="localSchemaRows.length === 0">
            <td
              class="py-4 ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4 text-body-main"
              colspan="5"
            >
              <p class="text-xs text-center text-n-slate-10 italic py-3 w-full">
                {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.NO_PROPERTIES') }}
              </p>
            </td>
          </tr>
          <tr v-for="(prop, index) in localSchemaRows" :key="index">
            <td class="py-4 ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4 text-body-main">
              <InputNext
                :model-value="prop.name"
                :message="propertyNameError(prop.name)"
                message-type="error"
                :placeholder="
                  t(
                    'WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.PROPERTY_NAME_PLACEHOLDER'
                  )
                "
                @update:model-value="updateProperty(index, 'name', $event)"
              />
            </td>
            <td class="py-4 ltr:pr-3 rtl:pl-3 text-body-main">
              <Select
                :model-value="prop.type"
                :options="PROPERTY_TYPES"
                class="[&_select]:bg-n-alpha-black2"
                @update:model-value="updateProperty(index, 'type', $event)"
              />
            </td>
            <td class="py-4 ltr:pr-3 rtl:pl-3 text-body-main">
              <InputNext
                :model-value="prop.label"
                :placeholder="
                  t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.LABEL_PLACEHOLDER')
                "
                @update:model-value="updateProperty(index, 'label', $event)"
              />
            </td>
            <td class="py-4 ltr:pr-3 rtl:pl-3 text-body-main">
              <InputNext
                :model-value="prop.description"
                :placeholder="
                  t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.DESCRIPTION_PLACEHOLDER')
                "
                @update:model-value="
                  updateProperty(index, 'description', $event)
                "
              />
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 text-body-main">
              <V4Button
                xs
                ghost
                slate
                icon="i-lucide-trash-2"
                @click="removeProperty(index)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <section>
      <div class="flex items-center justify-between mb-3">
        <div>
          <h4 class="text-heading-3 text-n-slate-12">
            {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.METRICS_TITLE') }}
          </h4>
          <p class="text-body-main text-n-slate-11">
            {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.METRICS_DESCRIPTION') }}
          </p>
        </div>
        <V4Button
          xs
          ghost
          blue
          icon="i-lucide-plus"
          :label="t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.ADD_METRIC')"
          @click="addMetric"
        />
      </div>

      <table
        class="min-w-full table-auto outline outline-1 -outline-offset-1 outline-n-strong rounded-xl"
      >
        <thead>
          <tr class="border-b border-n-strong">
            <th
              class="py-3 ltr:pl-4 rtl:pr-4 ltr:pr-4 rtl:pl-4 text-start text-heading-3 text-n-slate-12"
            >
              {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.METRIC_LABEL') }}
            </th>
            <th
              class="py-3 ltr:pr-4 rtl:pl-4 text-start text-heading-3 text-n-slate-12"
            >
              {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.METRIC_KEY') }}
            </th>
            <th
              class="py-3 ltr:pr-4 rtl:pl-4 text-start text-heading-3 text-n-slate-12"
            >
              {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.AGGREGATION') }}
            </th>
            <th
              class="py-3 ltr:pr-4 rtl:pl-4 text-start text-heading-3 text-n-slate-12"
            >
              {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.METRIC_PROPERTY') }}
            </th>
            <th class="py-3 ltr:pr-4 rtl:pl-4" />
          </tr>
        </thead>
        <tbody>
          <tr v-if="metrics.length === 0">
            <td
              class="py-4 ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4 text-body-main"
              colspan="5"
            >
              <p class="text-xs text-center text-n-slate-10 italic py-3 w-full">
                {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.NO_METRICS') }}
              </p>
            </td>
          </tr>
          <tr v-for="(metric, index) in metrics" :key="index">
            <td class="py-4 ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4 text-body-main">
              <InputNext
                :model-value="metric.label"
                :placeholder="
                  t(
                    'WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.METRIC_LABEL_PLACEHOLDER'
                  )
                "
                @update:model-value="updateMetric(index, 'label', $event)"
              />
            </td>
            <td class="py-4 ltr:pr-3 rtl:pl-3 text-body-main">
              <InputNext
                :model-value="metric.key"
                :placeholder="
                  t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.METRIC_KEY_PLACEHOLDER')
                "
                @update:model-value="updateMetric(index, 'key', $event)"
              />
            </td>
            <td class="py-4 ltr:pr-3 rtl:pl-3 text-body-main">
              <Select
                :model-value="metric.aggregation"
                :options="AGGREGATION_OPTIONS"
                class="[&_select]:bg-n-alpha-black2"
                @update:model-value="updateMetric(index, 'aggregation', $event)"
              />
            </td>
            <td class="py-4 ltr:pr-3 rtl:pl-3 text-body-main">
              <Select
                v-if="metric.aggregation !== 'count'"
                :model-value="metric.property"
                :options="propertyNames"
                :placeholder="
                  t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.SELECT_PROPERTY')
                "
                class="[&_select]:bg-n-alpha-black2"
                @update:model-value="updateMetric(index, 'property', $event)"
              />
              <span v-else class="i-lucide-minus w-4 h-4 text-n-slate-9" />
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 text-body-main">
              <V4Button
                xs
                ghost
                slate
                icon="i-lucide-trash-2"
                @click="removeMetric(index)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <section>
      <div class="flex items-center justify-between mb-3">
        <div>
          <h4 class="text-heading-3 text-n-slate-12">
            {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.DIMENSIONS_TITLE') }}
          </h4>
          <p class="text-body-main text-n-slate-11">
            {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.DIMENSIONS_DESCRIPTION') }}
          </p>
        </div>
        <V4Button
          xs
          ghost
          blue
          icon="i-lucide-plus"
          :label="t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.ADD_DIMENSION')"
          @click="addDimension"
        />
      </div>

      <table
        class="min-w-full table-auto outline outline-1 -outline-offset-1 outline-n-strong rounded-xl"
      >
        <thead>
          <tr class="border-b border-n-strong">
            <th
              class="py-3 ltr:pl-4 rtl:pr-4 ltr:pr-4 rtl:pl-4 text-start text-heading-3 text-n-slate-12"
            >
              {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.DIMENSION_SOURCE') }}
            </th>
            <th
              class="py-3 ltr:pr-4 rtl:pl-4 text-start text-heading-3 text-n-slate-12"
            >
              {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.DIMENSION_LABEL') }}
            </th>
            <th class="py-3 ltr:pr-4 rtl:pl-4" />
          </tr>
        </thead>
        <tbody>
          <tr v-if="dimensions.length === 0">
            <td
              class="py-4 ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4 text-body-main"
              colspan="3"
            >
              <p class="text-xs text-center text-n-slate-10 italic py-3 w-full">
                {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.NO_DIMENSIONS') }}
              </p>
            </td>
          </tr>
          <tr v-for="(dim, index) in dimensions" :key="index">
            <td class="py-4 ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4 text-body-main">
              <Select
                :model-value="dim.property || dim.source || ''"
                :options="dimensionOptions"
                :placeholder="
                  t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.SELECT_DIMENSION')
                "
                class="[&_select]:bg-n-alpha-black2"
                @update:model-value="updateDimension(index, $event)"
              />
            </td>
            <td class="py-4 ltr:pr-3 rtl:pl-3 text-body-main">
              <InputNext
                :model-value="dim.label"
                :placeholder="
                  t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.LABEL_PLACEHOLDER')
                "
                @update:model-value="updateDimensionLabel(index, $event)"
              />
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 text-body-main">
              <V4Button
                xs
                ghost
                slate
                icon="i-lucide-trash-2"
                @click="removeDimension(index)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <section>
      <div class="flex items-center justify-between mb-3">
        <h4 class="text-heading-3 text-n-slate-12">
          {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.DEFAULTS_TITLE') }}
        </h4>
      </div>
      <div class="grid grid-cols-2 gap-4">
        <div class="flex items-baseline gap-2">
          <label class="text-n-slate-11 text-heading-3 shrink-0">
            {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.CHART_TYPE') }}
          </label>
          <Select
            v-model="defaultVisualization"
            :options="CHART_TYPES"
            class="[&>select]:!p-0 min-w-24 [&>select]:!outline-none"
          />
        </div>
        <div class="flex items-baseline gap-2">
          <label class="text-n-slate-11 text-heading-3 shrink-0">
            {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.TIME_GRANULARITY') }}
          </label>
          <Select
            v-model="defaultTimeGranularity"
            :options="TIME_GRANULARITIES"
            class="[&>select]:!p-0 min-w-24 [&>select]:!outline-none"
          />
        </div>
        <div class="flex items-baseline gap-2">
          <label class="text-n-slate-11 text-heading-3 shrink-0">
            {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.DEFAULT_GROUP_BY') }}
          </label>
          <Select
            v-model="defaultGroupBy"
            :options="groupByOptions"
            class="[&>select]:!p-0 min-w-24 [&>select]:!outline-none"
          />
        </div>
        <div class="flex items-baseline gap-2">
          <label class="text-n-slate-11 text-heading-3 shrink-0">
            {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.VALUE_MODE') }}
          </label>
          <Select
            v-model="defaultValueMode"
            :options="VALUE_MODES"
            class="[&>select]:!p-0 min-w-24 [&>select]:!outline-none"
          />
        </div>
        <div class="flex items-center gap-2">
          <label class="text-n-slate-11 text-heading-3 shrink-0">
            {{ t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.SHOW_SUMMARY_CARDS') }}
          </label>
          <Switch v-model="showSummaryCards" />
        </div>
      </div>
    </section>

    <section v-if="webhookExampleJson">
      <div class="flex items-center justify-between mb-3">
        <h4 class="text-heading-3 text-n-slate-12">
          {{ t('WGPT_CUSTOM_REPORTS.GENERATE.WEBHOOK_EXAMPLE') }}
        </h4>
      </div>
      <woot-code lang="json" :script="webhookExampleJson" />
    </section>
  </div>
</template>
